const PREPOSICIONES = new Set([
  'de',
  'del',
  'la',
  'las',
  'los',
  'y',
  'en'
]);

/**
 * Convierte un texto en MAYÚSCULAS a formato editorial para direcciones:
 * - Mayúscula inicial en cada palabra
 * - Preposiciones en minúscula (salvo si son la primera palabra)
 * - Mantiene números y símbolos
 *
 * Ej:
 *  "AVENIDA PRESIDENTE PERON 123"
 *    -> "Avenida Presidente Perón 123"
 *
 *  "CALLE DE LOS TILOS"
 *    -> "Calle de los Tilos"
 */
export function normalizarTexto(Texto: string): string {
  if (!Texto) return Texto;

  const palabras = Texto.toLowerCase().split(' ');

  return palabras
    .map((palabra, index) => {
      // Mantener números tal cual
      if (/^\d+$/.test(palabra)) return palabra;

      // Excepción: siglas que deben quedar en mayúsculas
      if (palabra === 'caba') {
        return 'CABA';
      }

      // Preposición en minúscula si no es la primera palabra
      if (index !== 0 && PREPOSICIONES.has(palabra)) {
        return palabra;
      }

      // Capitalizar palabra normal
      return palabra.charAt(0).toUpperCase() + palabra.slice(1);
    })
    .join(' ');
}

/**
 * Diccionario de palabras que requieren tilde
 * Clave: forma SIN tilde
 * Valor: forma CORRECTA con tilde
 */
const TILDES_FIJAS: Record<string, string> = {
  hipolito: 'Hipólito',
  esquiu: 'Esquiú',
  neuquen: 'Neuquén',
  rio: 'Río',
  jose: 'José',
  "san martin": 'San Martín',
  belgrano: 'Belgrano', // ejemplo, sin tilde a propósito
  galeria: 'galería', // ejemplo, sin tilde a propósito
  parabolicos: 'parabólicos', // ejemplo, sin tilde a propósito
  galpon: 'Galpón' // ejemplo, sin tilde a propósito
};

/**
 * Corrige tildes conocidas dentro de una dirección ya normalizada
 *
 * Ej:
 *  "Avenida Hipolito Yrigoyen"
 *    -> "Avenida Hipólito Yrigoyen"
 */
export function corregirTildesTexto(texto: string): string {
  if (!texto) return texto;

  let resultado = texto;

  Object.entries(TILDES_FIJAS).forEach(([sinTilde, conTilde]) => {
    const regex = new RegExp(`\\b${sinTilde}\\b`, 'gi');
    resultado = resultado.replace(regex, conTilde);
  });

  return resultado;
}

export function sentenceCase(texto: string): string {
  if (!texto) return texto;
  const lower = texto.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function extraerLatLngDeGoogleMaps(
  url?: string
): { lat: number; lng: number } | null {
  if (!url) return null;

  // Caso 1: links tipo .../@-34.977616,-58.3777485,17z
  const matchArroba = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (matchArroba) {
    return {
      lat: parseFloat(matchArroba[1]),
      lng: parseFloat(matchArroba[2]),
    };
  }

  // Caso 2: links tipo ...!3d-34.9776204!4d-58.3755598
  const match3d4d = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (match3d4d) {
    return {
      lat: parseFloat(match3d4d[1]),
      lng: parseFloat(match3d4d[2]),
    };
  }

  return null;
}

export function formatearMonedaARS(valor: number | string): string {
  const numero =
    typeof valor === 'string'
      ? Number(valor)
      : valor;

  if (Number.isNaN(numero)) {
    return String(valor);
  }

  return (
    '$' +
    new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numero)
  );
}

export function formatearFecha(fecha?: string) {
  if (!fecha) return '';

  const [anio, mes, dia] = fecha.split('-');

  return `${dia}/${mes}/${anio}`;
}

export function formatearFechaISO(fecha?: string) {
  if (!fecha) return null;

  const [dia, mes, anio] = fecha.split('/');

  return `${anio}-${mes}-${dia}T00:00:00.000Z`;
}